import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Compass, MoreVertical, HelpCircle, Download, Upload, CheckCircle2, PlusCircle, Pencil, Trash2, Plus, ListChecks } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DATA_TOUR_IDS } from "@/components/tours/data-tour-ids.generated";
import { PageHeader } from "@/components/page-header";
import { usePageTitle } from "@/hooks/use-page-title";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AudienceNav, AudienceNavMobile } from "@/components/admin-tours/audience-nav";
import { ToursTab } from "@/components/admin-tours/tours-tab";
import { AudienceDialog } from "@/components/admin-tours/audience-dialog";
import { CsvImportDialog } from "@/components/ui/csv-import-dialog";
import type { NormalizedCsvPreview } from "@/components/ui/csv-import-dialog";
import type { TourAudienceRecord } from "@/components/admin-tours/types";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface TourCsvError {
  row: number;
  column: string;
  message: string;
}

interface TourImportPreviewResponse {
  errors: TourCsvError[];
  summary: {
    toursAdded: number;
    toursModified: number;
    toursUnchanged: number;
    toursNotInCsv: number;
    stepsAdded: number;
    stepsModified: number;
    stepsDeleted: number;
  };
  deletionWarning: string | null;
  staleWarning: string | null;
  preview: Array<{
    tourTitle: string;
    tourId: string | null;
    action: "add" | "update" | "unchanged";
    stepDiffs: Array<{
      stepSortOrder: number;
      action: "add" | "update" | "delete" | "unchanged";
    }>;
  }>;
}

// ── Anchor registry ─────────────────────────────────────────────────────────
// Filter out any codegen artefacts (template literals that leaked in)
const VALID_TOUR_IDS = (DATA_TOUR_IDS as readonly string[]).filter(
  (id) => !id.includes("$") && !id.includes("{") && !id.includes("}"),
);

// Group by first one or two hyphen-separated segments so nav-sidebar-* items
// land in their own bucket instead of collapsing everything into "nav".
function buildGroupedTourIds(ids: readonly string[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const id of ids) {
    const parts = id.split("-");
    const prefix =
      parts.length > 2 && (parts[0] === "nav" || parts[0] === "button" || parts[0] === "input")
        ? `${parts[0]}-${parts[1]}`
        : parts[0];
    if (!map[prefix]) map[prefix] = [];
    map[prefix].push(id);
  }
  return map;
}

const GROUPED_TOUR_IDS = buildGroupedTourIds(VALID_TOUR_IDS);
const TOUR_ID_GROUPS = Object.entries(GROUPED_TOUR_IDS).sort(([a], [b]) => a.localeCompare(b));

const toursCsvHelpContent = (
  <div className="space-y-3 text-xs">
    <p>
      <strong className="text-foreground">Round-trip workflow:</strong> Export the current tours
      to CSV, edit the file in a spreadsheet, then import it back. Only tours present in the
      CSV are upserted — tours not in the CSV are left untouched.
    </p>
    <p>
      <strong className="text-foreground">Steps are fully replaced:</strong> For each tour
      in the CSV, all existing steps are deleted and re-inserted from the CSV. Any step not in
      the CSV will be removed for that tour.
    </p>
    <p>
      <strong className="text-foreground">Audience slugs must exist:</strong> The{" "}
      <code className="font-mono">audience_slug</code> column must match an audience slug
      already configured in the system. Import will fail if any slug is unrecognised.
    </p>
    <p>
      <strong className="text-foreground">Step-less tours:</strong> A row with all step fields
      blank represents a tour with no steps (header-only tour). Export always emits one such
      row for step-less tours so they round-trip correctly.
    </p>
    <div>
      <p className="font-medium text-foreground mb-1">Key columns</p>
      <table className="w-full text-xs border-collapse">
        <tbody>
          {[
            ["tour_id", "Leave blank for new tours; supply to update an existing tour."],
            ["is_active", "true / false (blank = false)."],
            ["step_sort_order", "Required for step rows; non-negative integer; unique per tour."],
            ["step_type", '"highlight" (default) or "chapter". Chapter steps appear as a centered fullscreen card — no target element needed.'],
            ["step_target_id", "data-tour value. Required for highlight steps; supply exactly one of target_id or target_selector. Omit for chapter steps."],
            ["step_skip_beacon", 'true skips the pulsing dot that normally appears on the target before the step opens. Set to true for all steps in a sequential tour.'],
            ["step_pre_action", 'JSON object run before the step opens, e.g. {"kind":"click","selector":"#btn","settleMs":150}. Blank = none.'],
            ["step_placement", "auto · top · bottom · left · right · center (blank = auto)."],
            ["step_visibility_criteria", 'JSON object. e.g. {"roles":["behavior"]} or {"milestones":{"lacks":["onboarded"]}}. Blank = no restriction.'],
          ].map(([col, desc]) => (
            <tr key={col} className="border-b border-border/40">
              <td className="pr-3 py-0.5 font-mono whitespace-nowrap text-foreground">{col}</td>
              <td className="py-0.5">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function AdminTours() {
  usePageTitle("Tours");
  const { toast } = useToast();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [audienceDialogOpen, setAudienceDialogOpen] = useState(false);
  const [editingAudience, setEditingAudience] = useState<TourAudienceRecord | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<TourImportPreviewResponse | null>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const openTourRef = useRef<(() => void) | null>(null);

  const audiencesQuery = useQuery<{ audiences: TourAudienceRecord[] }>({
    queryKey: ["/api/admin/tour-audiences"],
  });

  const audiences = useMemo(
    () => audiencesQuery.data?.audiences ?? [],
    [audiencesQuery.data],
  );

  useEffect(() => {
    if (!selectedId && audiences.length > 0) {
      const def = audiences.find((a) => a.isDefault) ?? audiences[0];
      setSelectedId(def.id);
    }
  }, [selectedId, audiences]);

  const selected = useMemo(
    () => audiences.find((a) => a.id === selectedId) ?? null,
    [audiences, selectedId],
  );

  function openNewAudience() {
    setEditingAudience(null);
    setAudienceDialogOpen(true);
  }

  function openEditAudience(a: TourAudienceRecord) {
    setEditingAudience(a);
    setAudienceDialogOpen(true);
  }

  async function handleToursPreview(file: File): Promise<NormalizedCsvPreview> {
    setImportPreview(null);
    const formData = new FormData();
    formData.append("file", file);
    const data = (await apiRequest("POST", "/api/admin/tours/import/preview", formData)) as TourImportPreviewResponse;
    setImportPreview(data);
    const { summary } = data;
    const hasChanges =
      summary.toursAdded > 0 ||
      summary.toursModified > 0 ||
      summary.stepsAdded > 0 ||
      summary.stepsModified > 0 ||
      summary.stepsDeleted > 0;
    return {
      errors: data.errors.map((e) => ({
        row: e.row,
        message: e.column ? `${e.column}: ${e.message}` : e.message,
      })),
      staleWarning: data.staleWarning,
      deletionWarning: data.deletionWarning,
      hasChanges,
    };
  }

  async function handleToursCommit(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    const data = (await apiRequest("POST", "/api/admin/tours/import/commit", formData)) as {
      ok: true;
      toursUpserted: number;
      stepsInserted: number;
    };
    queryClient.invalidateQueries({ queryKey: ["/api/admin/tours"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/tour-audiences"] });
    const parts: string[] = [];
    if (data.toursUpserted > 0)
      parts.push(`${data.toursUpserted} tour${data.toursUpserted !== 1 ? "s" : ""} updated`);
    if (data.stepsInserted > 0)
      parts.push(`${data.stepsInserted} step${data.stepsInserted !== 1 ? "s" : ""} imported`);
    toast({
      title: "Import complete",
      description: parts.length > 0 ? parts.join(", ") : "No changes applied",
    });
  }

  function toursDiffSummary(_normalized: NormalizedCsvPreview) {
    if (!importPreview) return null;
    const { summary, preview } = importPreview;
    const hasChanges =
      summary.toursAdded > 0 ||
      summary.toursModified > 0 ||
      summary.stepsAdded > 0 ||
      summary.stepsModified > 0 ||
      summary.stepsDeleted > 0;

    if (!hasChanges) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
          No changes detected. Tours are already up to date.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-sm">
          <div>
            Tours added:{" "}
            <strong className="text-green-600 dark:text-green-400">{summary.toursAdded}</strong>
          </div>
          <div>
            Steps added:{" "}
            <strong className="text-green-600 dark:text-green-400">{summary.stepsAdded}</strong>
          </div>
          <div>
            Tours modified:{" "}
            <strong className="text-blue-600 dark:text-blue-400">{summary.toursModified}</strong>
          </div>
          <div>
            Steps modified:{" "}
            <strong className="text-blue-600 dark:text-blue-400">{summary.stepsModified}</strong>
          </div>
          <div>Tours unchanged: <strong>{summary.toursUnchanged}</strong></div>
          <div>
            Steps deleted:{" "}
            <strong className={summary.stepsDeleted > 0 ? "text-destructive" : ""}>{summary.stepsDeleted}</strong>
          </div>
          {summary.toursNotInCsv > 0 && (
            <div className="col-span-2 text-muted-foreground">
              {summary.toursNotInCsv} tour{summary.toursNotInCsv !== 1 ? "s" : ""} not in CSV (will not be changed)
            </div>
          )}
        </div>
        {preview.length > 0 && (
          <div className="space-y-0.5 border rounded-md divide-y divide-border/60 overflow-hidden">
            {preview.map((p, i) => {
              const stepsAdded = p.stepDiffs.filter((d) => d.action === "add").length;
              const stepsModified = p.stepDiffs.filter((d) => d.action === "update").length;
              const stepsDeleted = p.stepDiffs.filter((d) => d.action === "delete").length;
              return (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-1.5 text-sm gap-3"
                >
                  <span className="font-medium truncate min-w-0">{p.tourTitle}</span>
                  <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
                    {p.action === "add" && (
                      <span className="text-green-600 dark:text-green-400 font-medium">new tour</span>
                    )}
                    {p.action === "update" && (
                      <span className="text-blue-600 dark:text-blue-400 font-medium">modified</span>
                    )}
                    {p.action === "unchanged" && <span>unchanged</span>}
                    {stepsAdded > 0 && (
                      <span className="flex items-center gap-0.5 text-green-600 dark:text-green-400">
                        <PlusCircle className="h-3 w-3" />
                        {stepsAdded}
                      </span>
                    )}
                    {stepsModified > 0 && (
                      <span className="flex items-center gap-0.5 text-blue-600 dark:text-blue-400">
                        <Pencil className="h-3 w-3" />
                        {stepsModified}
                      </span>
                    )}
                    {stepsDeleted > 0 && (
                      <span className="flex items-center gap-0.5 text-destructive">
                        <Trash2 className="h-3 w-3" />
                        {stepsDeleted}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <AudienceNav
        audiences={audiences}
        isLoading={audiencesQuery.isLoading}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCreate={openNewAudience}
        onEdit={openEditAudience}
      />
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="px-4 sm:px-6 pb-6 pt-4">
          <PageHeader
            title={
              <div className="flex items-center gap-2" data-testid="heading-admin-tours">
                <Compass className="h-6 w-6 text-primary" />
                <span>Guided Tours</span>
              </div>
            }
            breadcrumb={[
              { label: "Administration" },
              { label: "Guided Tours" },
            ]}
            actions={
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" data-testid="button-tours-actions">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setInstructionsOpen(true)}
                      data-testid="menu-tours-instructions"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Instructions
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { window.location.href = "/api/admin/tours/export.csv"; }}
                      data-testid="menu-tours-export-csv"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setImportDialogOpen(true)}
                      data-testid="menu-tours-import-csv"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { window.location.href = "/admin/tours/audit"; }}
                      data-testid="menu-tours-open-audit"
                    >
                      <ListChecks className="w-4 h-4 mr-2" />
                      Open audit
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  onClick={() => openTourRef.current?.()}
                  data-testid="button-add-tour"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add tour
                </Button>
              </div>
            }
          />
          <p className="text-muted-foreground mb-6">
            Build guided product tours for staff. Each <strong>audience</strong>
            {" "}targets a group of users; each <strong>tour</strong> attaches to
            a page route; each <strong>step</strong> highlights a UI element.
          </p>
          <AudienceNavMobile
            audiences={audiences}
            isLoading={audiencesQuery.isLoading}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={() => {
              setEditingAudience(null);
              setAudienceDialogOpen(true);
            }}
            onEdit={(a) => {
              setEditingAudience(a);
              setAudienceDialogOpen(true);
            }}
          />
          {audiencesQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : selected ? (
            <ToursTab audience={selected} openTourRef={openTourRef} />
          ) : (
            <div className="border border-dashed rounded-md p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No audiences yet. Create one to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      <AudienceDialog
        open={audienceDialogOpen}
        onOpenChange={setAudienceDialogOpen}
        audience={editingAudience}
      />

      <CsvImportDialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) setImportPreview(null);
        }}
        title="Import Tours CSV"
        description="Upload a tours CSV to upsert tours and their steps. Tours not present in the CSV will not be affected."
        onPreview={handleToursPreview}
        onCommit={handleToursCommit}
        diffSummary={toursDiffSummary}
        helpContent={toursCsvHelpContent}
        commitLabel="Import Tours"
      />

      <Dialog open={instructionsOpen} onOpenChange={setInstructionsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-tours-instructions">
          <DialogHeader>
            <DialogTitle>Editing Tours via Spreadsheet</DialogTitle>
            <DialogDescription>
              How to use Export and Import to create, modify, or delete guided tours and their steps.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium">How tours are structured</h4>
              <p className="text-muted-foreground">
                An <strong>audience</strong> defines who sees a set of tours (e.g., "Behavior Team").
                A <strong>tour</strong> is attached to a page route and contains one or more{" "}
                <strong>steps</strong>. Steps come in two types: a <strong>highlight</strong> step
                points to a specific UI element and displays a tooltip, while a{" "}
                <strong>chapter</strong> step appears as a centered full-screen card with no element
                target — used to introduce or close a tour. In the CSV, each row represents one
                step; tour-level fields are repeated on every row belonging to that tour.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Overview</h4>
              <p className="text-muted-foreground">
                Export the current tours to CSV, open in Google Sheets or Excel, make your changes,
                then import the updated file. The import is an <strong>upsert</strong> — only tours
                present in the CSV are affected. Tours not listed are left untouched. Within each
                tour in the CSV, steps are <strong>fully replaced</strong>: all existing steps for
                that tour are removed and re-created from the CSV rows.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Step-by-step workflow</h4>
              <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
                <li>Click <strong>Export CSV</strong> to download the current tours.</li>
                <li>Open the file in Google Sheets or Excel.</li>
                <li>Make your changes (see sections below).</li>
                <li>Save / Download as a <strong>.csv</strong> file.</li>
                <li>Click <strong>Import CSV</strong> and select the file.</li>
                <li>Review the change summary, then click <strong>Import Tours</strong> to apply.</li>
              </ol>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Adding a new tour</h4>
              <p className="text-muted-foreground">
                Add a new block of rows at the bottom of the file. Leave <code className="font-mono text-xs">tour_id</code> blank
                — the system will assign a new ID. Set <code className="font-mono text-xs">tour_title</code>,{" "}
                <code className="font-mono text-xs">route_pattern</code>, and <code className="font-mono text-xs">audience_slug</code>.
                For each step, fill in <code className="font-mono text-xs">step_sort_order</code> and{" "}
                <code className="font-mono text-xs">step_body</code>. For <strong>highlight</strong> steps
                (the default), also supply exactly one of{" "}
                <code className="font-mono text-xs">step_target_id</code> or{" "}
                <code className="font-mono text-xs">step_target_selector</code>. For{" "}
                <strong>chapter</strong> steps, set <code className="font-mono text-xs">step_type</code>{" "}
                to <code className="font-mono text-xs">chapter</code> and leave both target columns blank.
                Repeat the tour-level fields on every step row.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Editing an existing tour</h4>
              <p className="text-muted-foreground">
                Keep the <code className="font-mono text-xs">tour_id</code> value intact — that is how the
                system knows which tour to update. Edit any other column freely. To reorder steps,
                change their <code className="font-mono text-xs">step_sort_order</code> values (must be
                unique per tour, non-negative integers). To add a step, insert a new row with the tour's
                repeated fields. To remove a step, delete its row.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Step-less tours</h4>
              <p className="text-muted-foreground">
                A tour with no steps is represented by a single row where all{" "}
                <code className="font-mono text-xs">step_*</code> columns are blank. The export
                automatically writes this format for any step-less tours, so they survive a round-trip.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Step types and targeting</h4>
              <p className="text-muted-foreground">
                The <code className="font-mono text-xs">step_type</code> column controls how a step
                is rendered. Leave it blank or set it to{" "}
                <code className="font-mono text-xs">highlight</code> for a standard tooltip anchored
                to a UI element. Set it to <code className="font-mono text-xs">chapter</code> for a
                centered full-screen card — chapter steps need no target element and ignore{" "}
                <code className="font-mono text-xs">step_placement</code>.
              </p>
              <p className="text-muted-foreground">
                For <strong>highlight</strong> steps, supply exactly one of{" "}
                <code className="font-mono text-xs">step_target_id</code> (the element's{" "}
                <code className="font-mono text-xs">data-tour</code> attribute value) or{" "}
                <code className="font-mono text-xs">step_target_selector</code> (a CSS selector),
                never both. Valid <code className="font-mono text-xs">step_placement</code> values:
                {" "}<code className="font-mono text-xs">auto</code>,{" "}
                <code className="font-mono text-xs">top</code>,{" "}
                <code className="font-mono text-xs">bottom</code>,{" "}
                <code className="font-mono text-xs">left</code>,{" "}
                <code className="font-mono text-xs">right</code>,{" "}
                <code className="font-mono text-xs">center</code>. Leave blank for{" "}
                <code className="font-mono text-xs">auto</code>.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Visibility criteria</h4>
              <p className="text-muted-foreground">
                <code className="font-mono text-xs">step_visibility_criteria</code> accepts a JSON object
                restricting which users see the step. Leave blank to show to everyone. Must be a JSON
                object (curly braces), not an array. Supported fields:
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
                <span className="font-mono text-xs whitespace-nowrap">{`{"roles":[...]}`}</span>
                <span>User must have at least one of the listed role slugs (e.g. <code className="font-mono text-xs">behavior</code>, <code className="font-mono text-xs">admin</code>).</span>
                <span className="font-mono text-xs whitespace-nowrap">{`{"locations":[...]}`}</span>
                <span>User must belong to at least one of the listed location slugs.</span>
                <span className="font-mono text-xs whitespace-nowrap">{`{"milestones":{"has":[...]}}`}</span>
                <span>User must have <em>all</em> of the listed milestone keys (e.g. <code className="font-mono text-xs">onboarded</code>).</span>
                <span className="font-mono text-xs whitespace-nowrap">{`{"milestones":{"lacks":[...]}}`}</span>
                <span>User must have <em>none</em> of the listed milestone keys. Useful for showing a step only to users who have not yet completed onboarding.</span>
              </div>
              <p className="text-muted-foreground">
                Fields can be combined in one object:{" "}
                <code className="font-mono text-xs">{`{"roles":["behavior"],"milestones":{"lacks":["onboarded"]}}`}</code>.
                All conditions must be satisfied for the step to appear.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Column reference</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
                <span className="font-mono text-xs">tour_id</span>
                <span>Leave blank for new tours; supply the existing UUID to update.</span>
                <span className="font-mono text-xs">tour_title</span>
                <span>Required. Display name of the tour.</span>
                <span className="font-mono text-xs">tour_description</span>
                <span>Optional. A short description shown to the user before the tour starts.</span>
                <span className="font-mono text-xs">route_pattern</span>
                <span>Required. URL path the tour activates on (e.g. <code className="font-mono text-xs">/animals</code>). Supports wildcards.</span>
                <span className="font-mono text-xs">audience_slug</span>
                <span>Required. Must match a slug from the Audiences list (e.g. <code className="font-mono text-xs">behavior-team</code>).</span>
                <span className="font-mono text-xs">is_active</span>
                <span><code className="font-mono text-xs">true</code> or <code className="font-mono text-xs">false</code>. Inactive tours are never shown. Blank = false.</span>
                <span className="font-mono text-xs">tour_sort_order</span>
                <span>Display order among tours for the same audience. Non-negative integer.</span>
                <span className="font-mono text-xs">step_sort_order</span>
                <span>Order of this step within the tour. Non-negative integer, unique per tour. Leave blank for step-less tours.</span>
                <span className="font-mono text-xs">step_type</span>
                <span><code className="font-mono text-xs">highlight</code> (default) or <code className="font-mono text-xs">chapter</code>. Highlight steps anchor to a UI element; chapter steps appear as a centered full-screen card with no target.</span>
                <span className="font-mono text-xs">step_title</span>
                <span>Optional heading shown above the step body.</span>
                <span className="font-mono text-xs">step_body</span>
                <span>Required for step rows. The main instructional text (supports plain text).</span>
                <span className="font-mono text-xs">step_target_id</span>
                <span>The <code className="font-mono text-xs">data-tour</code> attribute value of the element to highlight. Mutually exclusive with <code className="font-mono text-xs">step_target_selector</code>. Leave blank for chapter steps.</span>
                <span className="font-mono text-xs">step_target_selector</span>
                <span>CSS selector of the element to highlight. Mutually exclusive with <code className="font-mono text-xs">step_target_id</code>. Leave blank for chapter steps.</span>
                <span className="font-mono text-xs">step_placement</span>
                <span>Tooltip placement: auto · top · bottom · left · right · center. Blank = auto. Ignored for chapter steps.</span>
                <span className="font-mono text-xs">step_skip_beacon</span>
                <span><code className="font-mono text-xs">true</code> skips the pulsing dot that normally appears on the target before the step opens. Set to <code className="font-mono text-xs">true</code> for all steps in a sequential tour — the beacon is only useful for standalone steps the user triggers independently. Blank = false.</span>
                <span className="font-mono text-xs">step_pre_action</span>
                <span>JSON object describing an action to run automatically before the step is shown — e.g. clicking a button to open a panel. Only <code className="font-mono text-xs">kind: "click"</code> is supported. Example: <code className="font-mono text-xs">{`{"kind":"click","selector":"#my-btn","settleMs":150}`}</code>. <code className="font-mono text-xs">settleMs</code> is how long to wait after the click before showing the step (0–2000). Blank = none. Optional column — old exports without this column still import correctly.</span>
                <span className="font-mono text-xs">step_visibility_criteria</span>
                <span>JSON object limiting which users see this step. Supports <code className="font-mono text-xs">roles</code>, <code className="font-mono text-xs">locations</code>, and <code className="font-mono text-xs">milestones</code> (<code className="font-mono text-xs">has</code> / <code className="font-mono text-xs">lacks</code>) keys. Blank = visible to all.</span>
                <span className="font-mono text-xs">step_source</span>
                <span>Optional. Provenance label (e.g. <code className="font-mono text-xs">manual</code>, <code className="font-mono text-xs">csv-import</code>).</span>
              </div>
            </div>

            {TOUR_ID_GROUPS.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Registered data-tour anchors</h4>
                <p className="text-muted-foreground">
                  These are the <code className="font-mono text-xs">data-tour</code> values wired up
                  in the app. Use any of these as <code className="font-mono text-xs">step_target_id</code>.
                  The list updates automatically as anchors are added to the codebase.
                </p>
                <div className="space-y-2.5">
                  {TOUR_ID_GROUPS.map(([prefix, ids]) => (
                    <div key={prefix}>
                      <p className="text-xs font-mono text-muted-foreground mb-1">{prefix}-*</p>
                      <div className="flex flex-wrap gap-1.5">
                        {ids.map((id) => (
                          <Badge
                            key={id}
                            variant="outline"
                            className="font-mono text-[11px] no-default-hover-elevate no-default-active-elevate cursor-default"
                          >
                            {id}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="font-medium">Tips</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Don't delete or rename the header row.</li>
                <li>Don't modify values in the <strong>tour_id</strong> column for existing tours — that is how updates are matched.</li>
                <li>Repeat all tour-level columns identically on every step row within the same tour.</li>
                <li>Tours not present in the CSV are completely untouched — you don't need to include every tour.</li>
                <li>Google Sheets may show TRUE/FALSE in uppercase — that's fine, the import handles it.</li>
                <li>The <code className="font-mono text-xs">#_exported_at</code> comment line at the top is ignored on import — leave it or remove it.</li>
                <li>Old exports that don't have a <code className="font-mono text-xs">step_pre_action</code> column still import correctly — it defaults to none for all steps.</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setInstructionsOpen(false)} data-testid="button-close-tours-instructions">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
